// AdminNewAccounts.tsx
// Phase 10 (2026-05-26) — admin review queue. Visible only to users
// with the `admin_review` permission (Phase 4 gating).
//
// Until the Phase 10 backend module is activated in Phase 17 this UI
// renders against a small in-memory mock so the surface and the
// permission gating can be browser-verified end-to-end. The data
// schema matches `AdminQueueItem` in backend_staging/admin_review.py.

import React, { useState } from 'react';
import { ShieldAlert, Mail, Check, X, FileText } from 'lucide-react';
import { useAuthenticatedUser, RESTRICTED_FEATURE_COPY } from '../hooks/useAuthenticatedUser';
import { MockupPreviewBanner } from './MockupPreviewBanner';

interface QueueItem {
  application_id: string;
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  submitted_at: string;
  risk_flag_codes: string[];
}

// Demo data used until Phase 17 wires the backend. All names + emails are
// fictional. No real applicant data.
const DEMO_QUEUE: QueueItem[] = [
  {
    application_id: 'demo-app-1',
    user_id: 'demo-user-1',
    email: 'newuser1@example.com',
    first_name: 'Alex',
    last_name: 'Sample',
    submitted_at: '2026-05-26T00:30:00+00:00',
    risk_flag_codes: ['unrealistic_goal', 'beginner_aggressive_goal'],
  },
  {
    application_id: 'demo-app-2',
    user_id: 'demo-user-2',
    email: 'newuser2@example.com',
    first_name: 'Riley',
    last_name: 'Example',
    submitted_at: '2026-05-26T01:10:00+00:00',
    risk_flag_codes: [],
  },
  {
    application_id: 'demo-app-3',
    user_id: 'demo-user-3',
    email: 'newuser3@example.com',
    first_name: 'Casey',
    last_name: 'Test',
    submitted_at: '2026-05-26T02:00:00+00:00',
    risk_flag_codes: ['wants_live_trading'],
  },
];

function SeverityChip({ code }: { code: string }) {
  const blocking = code === 'wants_live_trading' || code === 'refused_disclosures';
  const cls = blocking
    ? 'bg-rose-500/10 text-rose-700 border-rose-500/30'
    : 'bg-amber-500/10 text-amber-700 border-amber-500/30';
  return (
    <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded border ${cls}`}>
      {code}
    </span>
  );
}

export default function AdminNewAccounts() {
  const u = useAuthenticatedUser();

  if (!u.permissions.admin_review) {
    return (
      <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm" data-testid="admin-new-accounts-restricted">
        <MockupPreviewBanner screen='admin-new-accounts' />
        <div className="flex items-center gap-2 mb-3">
          <ShieldAlert size={18} className="text-rose-500" />
          <h3 className="text-lg font-black uppercase italic">Admin Review Queue</h3>
        </div>
        <div className="p-6 border-2 border-dashed border-[var(--input-border)] rounded-xl text-center space-y-1">
          <div className="text-sm font-black uppercase italic">{RESTRICTED_FEATURE_COPY.heading}</div>
          <div className="text-xs font-bold text-[var(--muted-text)]">Admin review is restricted to users with the admin status.</div>
        </div>
      </div>
    );
  }

  const [items, setItems] = useState<QueueItem[]>(DEMO_QUEUE);
  const [selected, setSelected] = useState<string | null>(null);
  const [decisionNote, setDecisionNote] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);

  const sel = items.find((i) => i.application_id === selected);

  function decide(application_id: string, decision: 'approved' | 'rejected' | 'more_info_requested') {
    setItems((prev) => prev.filter((i) => i.application_id !== application_id));
    setSelected(null);
    setFeedback(`Application ${application_id} → ${decision}`);
    setDecisionNote('');
  }

  return (
    <div className="space-y-4" data-testid="admin-new-accounts">
        <MockupPreviewBanner screen='admin-new-accounts' />
      <div className="flex items-center gap-2">
        <ShieldAlert size={18} className="text-emerald-600" />
        <h3 className="text-lg font-black uppercase italic">Admin Review Queue · newaccounts@adversyn.com</h3>
      </div>
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs font-bold text-emerald-700" data-testid="admin-email-note">
        <Mail size={12} className="inline-block mr-1" />
        Completed applications are emailed to <code className="font-mono">newaccounts@adversyn.com</code>. The email never contains passwords, API secrets, MFA secrets, backup codes, tokens, SSN, Tax ID, or DOB.
      </div>

      {feedback && (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-2 text-xs font-bold text-emerald-700" data-testid="admin-feedback">
          {feedback}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Queue */}
        <section className="md:col-span-2 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-4 shadow-sm" data-testid="admin-queue-list">
          <div className="text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)] mb-2">
            Pending applications ({items.length})
          </div>
          {items.length === 0 ? (
            <div className="text-xs font-bold text-[var(--muted-text)]" data-testid="admin-queue-empty">
              No pending applications.
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((it) => (
                <button
                  key={it.application_id}
                  onClick={() => setSelected(it.application_id)}
                  data-testid={`admin-queue-item-${it.application_id}`}
                  className={
                    'w-full text-left rounded-xl border p-3 transition-colors ' +
                    (selected === it.application_id
                      ? 'border-emerald-500 bg-emerald-500/10'
                      : 'border-[var(--card-border)] bg-[var(--input-bg)] hover:border-emerald-500/50')
                  }
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-black uppercase italic text-[var(--app-text)]">
                        {it.first_name} {it.last_name}
                      </div>
                      <div className="text-[10px] font-bold text-[var(--muted-text)]">
                        {it.email} · submitted {it.submitted_at.replace('T', ' ').slice(0, 16)}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 justify-end">
                      {it.risk_flag_codes.length === 0 ? (
                        <span className="text-[10px] font-bold text-[var(--muted-text)]">no risk flags</span>
                      ) : it.risk_flag_codes.map((c) => <SeverityChip key={c} code={c} />)}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Detail / actions */}
        <section className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-4 shadow-sm" data-testid="admin-detail-panel">
          {!sel ? (
            <div className="text-xs font-bold text-[var(--muted-text)]" data-testid="admin-detail-empty">
              Select an application to review.
            </div>
          ) : (
            <div className="space-y-3" data-testid="admin-detail-loaded">
              <div className="text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)]">Application</div>
              <div className="text-sm font-black italic">{sel.first_name} {sel.last_name}</div>
              <div className="text-xs font-bold text-[var(--muted-text)]">{sel.email}</div>
              <div className="rounded-xl border border-[var(--input-border)] bg-[var(--input-bg)] p-3 text-[10px] font-bold text-[var(--muted-text)]">
                Application ID: <code className="font-mono">{sel.application_id}</code><br />
                User ID: <code className="font-mono">{sel.user_id}</code><br />
                Submitted: {sel.submitted_at}
              </div>
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)] mb-1">Risk flags</div>
                <div className="flex flex-wrap gap-1">
                  {sel.risk_flag_codes.length === 0 ? (
                    <span className="text-[10px] font-bold text-[var(--muted-text)]">none</span>
                  ) : sel.risk_flag_codes.map((c) => <SeverityChip key={c} code={c} />)}
                </div>
              </div>
              <label className="block">
                <span className="block mb-1 text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)]">Decision note</span>
                <textarea
                  data-testid="admin-decision-note"
                  value={decisionNote}
                  onChange={(e) => setDecisionNote(e.target.value)}
                  placeholder="Internal note (never emailed to applicant)"
                  className="w-full h-20 rounded-xl border border-[var(--input-border)] bg-[var(--input-bg)] px-3 py-2 text-xs"
                />
              </label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  data-testid="admin-action-approve"
                  onClick={() => decide(sel.application_id, 'approved')}
                  className="rounded-xl bg-emerald-600 hover:bg-emerald-500 px-3 py-2 text-[10px] font-black uppercase italic text-white"
                >
                  <Check size={12} className="inline-block mr-1" /> Approve
                </button>
                <button
                  data-testid="admin-action-reject"
                  onClick={() => decide(sel.application_id, 'rejected')}
                  className="rounded-xl bg-rose-600 hover:bg-rose-500 px-3 py-2 text-[10px] font-black uppercase italic text-white"
                >
                  <X size={12} className="inline-block mr-1" /> Reject
                </button>
                <button
                  data-testid="admin-action-more-info"
                  onClick={() => decide(sel.application_id, 'more_info_requested')}
                  className="rounded-xl bg-amber-500 hover:bg-amber-400 px-3 py-2 text-[10px] font-black uppercase italic text-white"
                >
                  <FileText size={12} className="inline-block mr-1" /> More Info
                </button>
              </div>
            </div>
          )}
        </section>
      </div>

      <div className="text-[10px] text-[var(--muted-text)] font-bold uppercase tracking-widest"
           data-testid="admin-no-secrets-footer">
        Email payload sanitization is enforced server-side (Phase 10 backend). UI never shows passwords / API secrets / MFA / tokens / SSN / DOB.
      </div>
    </div>
  );
}
